import { supabase, hasValidCredentials } from './supabase'
import type { Database } from './supabase'

// Type aliases
type InterviewRequestDb = Database['public']['Tables']['interview_requests']['Row']

// Frontend-compatible types
export interface InterviewRequest {
  id: string
  journalistId: string
  journalistName: string
  outlet: string
  celebrities: string[]
  status: 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined'
  priority: Record<string, 'A' | 'B' | 'C' | 'Unassigned'>
  journalistPriority: 'High' | 'Medium' | 'Low' | null
  completed: Record<string, boolean>
  createdAt: string
  updatedAt: string
}

// Conversion functions
const convertInterviewRequest = (dbRequest: InterviewRequestDb): InterviewRequest => ({
  id: dbRequest.id,
  journalistId: dbRequest.journalist_id || '',
  journalistName: dbRequest.journalist_name,
  outlet: dbRequest.outlet,
  celebrities: dbRequest.celebrities,
  status: dbRequest.status as 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined',
  priority: (dbRequest.priority as Record<string, 'A' | 'B' | 'C' | 'Unassigned'>) || {},
  journalistPriority: (dbRequest as any).journalist_priority as 'High' | 'Medium' | 'Low' | null,
  completed: (dbRequest.completed as Record<string, boolean>) || {},
  createdAt: dbRequest.created_at || '',
  updatedAt: dbRequest.updated_at || ''
})

// Interview Request service
export const interviewRequestService = {
  async getAll(eventYear?: number, journalistId?: string): Promise<InterviewRequest[]> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - returning empty interview requests array')
      return []
    }

    try {
      let query = supabase
        .from('interview_requests')
        .select('*')
      if (eventYear) query = query.eq('event_year', eventYear)
      if (journalistId) query = query.eq('journalist_id', journalistId)
      const { data, error } = await query.order('created_at', { ascending: false })

      if (error) throw error
      return (data || []).map(convertInterviewRequest)
    } catch (error) {
      console.error('Error fetching interview requests:', error)
      return []
    }
  },

  async getByJournalistId(journalistId: string): Promise<InterviewRequest[]> {
    if (!hasValidCredentials) {
      return []
    }

    try {
      const { data, error } = await supabase
        .from('interview_requests')
        .select('*')
        .eq('journalist_id', journalistId)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return (data || []).map(convertInterviewRequest)
    } catch (error) {
      console.error('Error fetching journalist interview requests:', error)
      return []
    }
  },

  async getByCelebrity(celebrityName: string): Promise<InterviewRequest[]> {
    if (!hasValidCredentials) {
      return []
    }

    try {
      const { data, error } = await supabase
        .from('interview_requests')
        .select('*')
        .contains('celebrities', [celebrityName])
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return (data || []).map(convertInterviewRequest)
    } catch (error) {
      console.error('Error fetching celebrity interview requests:', error)
      return []
    }
  },

  async create(request: Omit<InterviewRequest, 'id' | 'createdAt' | 'updatedAt'>, eventYear?: number): Promise<InterviewRequest | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot save interview request')
      return null
    }

    try {
      const insertData: any = {
          journalist_id: request.journalistId || null,
          journalist_name: request.journalistName,
          outlet: request.outlet,
          celebrities: request.celebrities,
          status: request.status || 'new',
          priority: request.priority || {},
          completed: request.completed || {}
      }
      if (eventYear) insertData.event_year = eventYear
      const { data, error } = await supabase
        .from('interview_requests')
        .insert(insertData)
        .select()
        .single()
      
      if (error) throw error
      return data ? convertInterviewRequest(data) : null
    } catch (error) {
      console.error('Error creating interview request:', error)
      throw error
    }
  },

  async updateStatus(id: string, status: 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined', userId?: string): Promise<InterviewRequest | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot update interview request')
      return null
    }

    try {
      // Check user permissions if userId is provided
      if (userId) {
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('role')
          .eq('id', userId)
          .single()
        
        if (userError || !user || !['pr', 'admin'].includes(user.role)) {
          console.error('Unauthorized: Only PR team can update interview status')
          throw new Error('Unauthorized: Only PR team members can update interview status')
        }
      }

      console.log('Attempting to update status to:', status, 'for request:', id)
      const { data, error } = await supabase
        .from('interview_requests')
        .update({ status })
        .eq('id', id)
        .select()
        .single()
      
      if (error) {
        console.error('Database error:', error)
        if (error.code === '23514') {
          throw new Error(`Status "${status}" is not allowed. Please check database constraints.`)
        }
        throw error
      }
      return data ? convertInterviewRequest(data) : null
    } catch (error) {
      console.error('Error updating interview request status:', error)
      throw error
    }
  },

  async updatePriority(id: string, celebrityName: string, priority: 'A' | 'B' | 'C' | 'Unassigned', userId?: string): Promise<InterviewRequest | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot update interview request')
      return null
    }

    try {
      // Check user permissions if userId is provided
      if (userId) {
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('role')
          .eq('id', userId)
          .single()
        
        if (userError || !user || !['pr', 'admin'].includes(user.role)) {
          console.error('Unauthorized: Only PR team can update interview priority')
          throw new Error('Unauthorized: Only PR team members can update interview priority')
        }
      }

      // First get the current request to update the priority object
      const { data: currentData, error: fetchError } = await supabase
        .from('interview_requests')
        .select('priority')
        .eq('id', id)
        .single()
      
      if (fetchError) throw fetchError

      const currentPriority = (currentData?.priority as Record<string, 'A' | 'B' | 'C' | 'Unassigned'>) || {}
      const updatedPriority = { ...currentPriority, [celebrityName]: priority }

      const { data, error } = await supabase
        .from('interview_requests')
        .update({ priority: updatedPriority })
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw error
      return data ? convertInterviewRequest(data) : null
    } catch (error) {
      console.error('Error updating interview request priority:', error)
      throw error
    }
  },

  async updateJournalistPriority(id: string, journalistPriority: 'High' | 'Medium' | 'Low' | null): Promise<InterviewRequest | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot update journalist priority')
      return null
    }

    try {
      const { data, error } = await supabase
        .from('interview_requests')
        .update({ journalist_priority: journalistPriority })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data ? convertInterviewRequest(data) : null
    } catch (error) {
      console.error('Error updating journalist priority:', error)
      throw error
    }
  },

  async markCompleted(id: string, celebrityName: string, completed: boolean, userId?: string): Promise<InterviewRequest | null> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot update interview request')
      return null
    }

    try {
      // Check user permissions if userId is provided
      if (userId) {
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('role')
          .eq('id', userId)
          .single()
        
        if (userError || !user || !['pr', 'admin'].includes(user.role)) {
          console.error('Unauthorized: Only PR team can mark interviews as completed')
          throw new Error('Unauthorized: Only PR team members can mark interviews as completed')
        }
      }

      // First get the current request to update the completed object
      const { data: currentData, error: fetchError } = await supabase
        .from('interview_requests')
        .select('completed')
        .eq('id', id)
        .single()
      
      if (fetchError) throw fetchError

      const currentCompleted = (currentData?.completed as Record<string, boolean>) || {}
      const updatedCompleted = { ...currentCompleted, [celebrityName]: completed }

      const { data, error } = await supabase
        .from('interview_requests')
        .update({ completed: updatedCompleted })
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw error
      return data ? convertInterviewRequest(data) : null
    } catch (error) {
      console.error('Error updating interview request completion:', error)
      throw error
    }
  },

  async delete(id: string): Promise<boolean> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot delete interview request')
      return false
    }

    try {
      const { error } = await supabase
        .from('interview_requests')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      return true
    } catch (error) {
      console.error('Error deleting interview request:', error)
      throw error
    }
  },

  async deleteByJournalistName(journalistName: string): Promise<number> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot delete interview requests by journalist')
      return 0
    }

    try {
      const { data, error } = await supabase
        .from('interview_requests')
        .delete()
        .eq('journalist_name', journalistName)
        .select()
      
      if (error) throw error
      return data?.length || 0
    } catch (error) {
      console.error('Error deleting interview requests by journalist:', error)
      throw error
    }
  },

  async cleanupOrphanedRequests(): Promise<number> {
    if (!hasValidCredentials) {
      console.warn('No Supabase credentials - cannot cleanup orphaned requests')
      return 0
    }

    try {
      // Get all existing journalist names
      const { data: journalists, error: journalistError } = await supabase
        .from('journalists')
        .select('name')
      
      if (journalistError) throw journalistError
      
      const existingJournalistNames = journalists?.map(j => j.name) || []
      
      // Get all interview requests
      const { data: requests, error: requestError } = await supabase
        .from('interview_requests')
        .select('id, journalist_name')
      
      if (requestError) throw requestError
      
      // Find orphaned requests (journalist name not in existing journalists)
      const orphanedRequests = requests?.filter(req => 
        !existingJournalistNames.includes(req.journalist_name)
      ) || []
      
      if (orphanedRequests.length === 0) {
        return 0
      }
      
      // Delete orphaned requests
      const orphanedIds = orphanedRequests.map(req => req.id)
      const { data, error: deleteError } = await supabase
        .from('interview_requests')
        .delete()
        .in('id', orphanedIds)
        .select()
      
      if (deleteError) throw deleteError
      
      console.log(`Cleaned up ${data?.length || 0} orphaned interview requests`)
      return data?.length || 0
    } catch (error) {
      console.error('Error cleaning up orphaned requests:', error)
      throw error
    }
  }
}